import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Sparkles, Info } from "lucide-react";

/**
 * "Push to AI Team" dialog. Iter 42.3 — extended to let the pusher pick
 * BOTH an AI Content Manager and/or an AI Team Lead:
 *   • Pick CM only → same behaviour as before (TL cleared / kept per `share`).
 *   • Pick TL only → CM auto-derived from the TL's hierarchy (server-side).
 *   • Pick both   → assign both.
 * Either the CM OR the TL must be picked before Push is enabled.
 */
export default function PushToAIDialog({ open, onClose, projects, onDone }) {
  const [aiCMs, setAiCMs] = useState([]);
  const [aiTLs, setAiTLs] = useState([]);
  const [loadingCMs, setLoadingCMs] = useState(false);
  const [loadingTLs, setLoadingTLs] = useState(false);
  const [selectedCM, setSelectedCM] = useState("");
  const [selectedTL, setSelectedTL] = useState("");
  const [share, setShare] = useState(false);
  const [cmSearch, setCmSearch] = useState("");
  const [tlSearch, setTlSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedCM(""); setSelectedTL("");
    setShare(false); setCmSearch(""); setTlSearch("");
    setLoadingCMs(true);
    api.get("/ai/content-managers")
      .then(({ data }) => setAiCMs(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingCMs(false));
  }, [open]);

  // Refetch AI TLs whenever the CM selection changes — filters to only TLs
  // reporting to that CM. If no CM is picked, show every AI TL and the
  // server auto-derives the CM from the TL's hierarchy on submit.
  useEffect(() => {
    if (!open) return;
    setLoadingTLs(true);
    const q = selectedCM ? `?manager_id=${encodeURIComponent(selectedCM)}` : "";
    api.get(`/ai/team-leads${q}`)
      .then(({ data }) => setAiTLs(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingTLs(false));
    // If the currently-picked TL doesn't report to the newly-picked CM,
    // clear the TL selection to avoid an inconsistent state.
    setSelectedTL((prev) => prev);
  }, [open, selectedCM]);

  const filteredCMs = useMemo(() => {
    const q = cmSearch.trim().toLowerCase();
    if (!q) return aiCMs;
    return aiCMs.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q));
  }, [aiCMs, cmSearch]);

  const filteredTLs = useMemo(() => {
    const q = tlSearch.trim().toLowerCase();
    if (!q) return aiTLs;
    return aiTLs.filter((t) => (t.name || "").toLowerCase().includes(q) || (t.email || "").toLowerCase().includes(q));
  }, [aiTLs, tlSearch]);

  const canSubmit = !!(selectedCM || selectedTL);

  const submit = async () => {
    if (!canSubmit) { toast.error("Pick an AI Content Manager or AI Team Lead"); return; }
    if (!projects || projects.length === 0) return;
    setBusy(true);
    let ok = 0;
    for (const p of projects) {
      try {
        await api.post(`/projects/${p.id}/push-to-ai`, {
          ai_content_manager_id: selectedCM || null,
          ai_team_lead_id: selectedTL || null,
          share,
        });
        ok += 1;
      } catch (e) { toast.error(`${p.name}: ${formatApiError(e)}`); }
    }
    setBusy(false);
    if (ok > 0) {
      toast.success(`${ok} project${ok === 1 ? "" : "s"} pushed to AI team`);
      onDone && onDone();
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose} data-testid="push-to-ai-dialog">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-indigo-50">
          <div className="flex items-center gap-2 text-purple-800 font-semibold">
            <Sparkles className="h-5 w-5" /> Push to AI Team
          </div>
          <div className="text-xs text-slate-600 mt-1">
            Handing off <strong>{projects.length}</strong> project{projects.length === 1 ? "" : "s"} to the AI team. Pick a Content Manager, a Team Lead, or both.
          </div>
        </div>

        <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto scrollbar-thin">
          {/* AI CM column */}
          <div>
            <label className="text-xs font-medium text-slate-700 flex items-center justify-between">
              <span>AI Content Manager <span className="text-slate-400">(optional if TL picked)</span></span>
              {selectedCM && <button onClick={() => setSelectedCM("")} className="text-[10px] text-slate-500 hover:text-purple-700" data-testid="ai-cm-clear">clear</button>}
            </label>
            <div className="mt-1.5 relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Search AI Content Managers…"
                value={cmSearch}
                onChange={(e) => setCmSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="ai-cm-search"
              />
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto scrollbar-thin border border-slate-200 rounded-lg divide-y divide-slate-100">
              {loadingCMs && <div className="text-center text-slate-400 text-xs py-4">Loading…</div>}
              {!loadingCMs && filteredCMs.length === 0 && (
                <div className="text-center text-slate-400 text-xs py-4 italic">No AI Content Managers. Tag a CM's Workflow as "AI-assisted" in the Team tab.</div>
              )}
              {!loadingCMs && filteredCMs.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedCM(c.id)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-purple-50 transition ${selectedCM === c.id ? "bg-purple-50 ring-1 ring-inset ring-purple-300" : ""}`}
                  data-testid={`ai-cm-option-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold whitespace-nowrap">
                    {c.project_count} project{c.project_count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* AI TL column */}
          <div>
            <label className="text-xs font-medium text-slate-700 flex items-center justify-between">
              <span>AI Team Lead <span className="text-slate-400">(optional if CM picked)</span></span>
              {selectedTL && <button onClick={() => setSelectedTL("")} className="text-[10px] text-slate-500 hover:text-purple-700" data-testid="ai-tl-clear">clear</button>}
            </label>
            <div className="mt-1.5 relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                placeholder={selectedCM ? "Team Leads under the picked CM…" : "Search all AI Team Leads…"}
                value={tlSearch}
                onChange={(e) => setTlSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="ai-tl-search"
              />
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto scrollbar-thin border border-slate-200 rounded-lg divide-y divide-slate-100">
              {loadingTLs && <div className="text-center text-slate-400 text-xs py-4">Loading…</div>}
              {!loadingTLs && filteredTLs.length === 0 && (
                <div className="text-center text-slate-400 text-xs py-4 italic">
                  {selectedCM
                    ? "No AI Team Leads under this Content Manager."
                    : "No AI Team Leads. Tag a TL's Workflow as \"AI-assisted\" in the Team tab."}
                </div>
              )}
              {!loadingTLs && filteredTLs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedTL(t.id)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-purple-50 transition ${selectedTL === t.id ? "bg-purple-50 ring-1 ring-inset ring-purple-300" : ""}`}
                  data-testid={`ai-tl-option-${t.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{t.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{t.email}</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold whitespace-nowrap">
                    {t.project_count} project{t.project_count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 pb-3 -mt-1 flex items-start gap-2 text-[11px] text-slate-500">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>Pick a TL and their Content Manager is derived automatically from your team hierarchy.</span>
        </div>

        <div className="px-6 pb-4">
          <label className="flex items-start gap-2 cursor-pointer" data-testid="ai-share-toggle-label">
            <input
              type="checkbox"
              checked={share}
              onChange={(e) => setShare(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              data-testid="ai-share-toggle"
            />
            <span className="text-sm text-slate-700">
              Keep current holders too <span className="text-slate-400">(share with AI team — default off is transfer)</span>
            </span>
          </label>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="push-to-ai-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !canSubmit}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="push-to-ai-confirm"
          >
            {busy ? "Pushing…" : `Push ${projects.length} to AI`}
          </Button>
        </div>
      </div>
    </div>
  );
}
